
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SitemapItem, SitemapSection, FilterCategory } from '@/types/sitemap';
import { SitemapTableRow } from './SitemapTableRow';
import { FilterButtons } from './FilterButtons';

interface SitemapTableProps {
  sections: SitemapSection[];
  selectedFilter: FilterCategory;
  onFilterChange: (filter: FilterCategory) => void;
}

type SortField = 'name' | 'category' | 'status' | 'url';
type SortDirection = 'asc' | 'desc';

interface FlattenedSitemapItem extends SitemapItem {
  category: string;
  categoryEmoji: string;
}

export const SitemapTable = ({ sections, selectedFilter, onFilterChange }: SitemapTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('category');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Flatten the sections data into table rows
  const flattenedData = useMemo(() => {
    const flattened: FlattenedSitemapItem[] = [];
    sections.forEach(section => {
      section.items.forEach(item => {
        flattened.push({
          ...item,
          category: section.title,
          categoryEmoji: section.emoji,
        });
      });
    });
    return flattened;
  }, [sections]);

  // Filter and search data
  const filteredData = useMemo(() => {
    let data = flattenedData;

    // Apply category filter
    if (selectedFilter !== 'all') {
      data = data.filter(item => {
        const sectionCategory = sections.find(s => s.title === item.category)?.category;
        return sectionCategory === selectedFilter;
      });
    }

    // Apply search filter
    if (searchTerm) {
      data = data.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return data;
  }, [flattenedData, selectedFilter, searchTerm, sections]);

  // Sort data
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aValue: string, bValue: string;
      
      switch (sortField) {
        case 'name':
          aValue = a.title;
          bValue = b.title;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'url':
          aValue = a.href;
          bValue = b.href;
          break;
        default:
          return 0;
      }

      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-juv-terracotta" />
      : <ArrowDown className="h-4 w-4 text-juv-terracotta" />;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <FilterButtons 
        selectedFilter={selectedFilter} 
        onFilterChange={onFilterChange} 
      />

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative max-w-md mx-auto"
      >
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search pages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-juv-rose-clay/20 focus:border-juv-terracotta bg-white"
        />
      </motion.div>

      {/* Results Count */}
      <div className="text-center text-sm text-gray-600">
        Showing {sortedData.length} of {flattenedData.length} pages
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-xl border border-juv-rose-clay/20 overflow-hidden shadow-sm"
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-juv-soft-blush/30 sticky top-0 z-10">
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-juv-soft-blush/50 transition-colors min-w-[200px]"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-juv-deep-chestnut">Page Name</span>
                    {getSortIcon('name')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-juv-soft-blush/50 transition-colors min-w-[150px]"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-juv-deep-chestnut">Category</span>
                    {getSortIcon('category')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-juv-soft-blush/50 transition-colors min-w-[120px]"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-juv-deep-chestnut">Status</span>
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-juv-soft-blush/50 transition-colors min-w-[150px]"
                  onClick={() => handleSort('url')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-juv-deep-chestnut">URL</span>
                    {getSortIcon('url')}
                  </div>
                </TableHead>
                <TableHead className="min-w-[250px]">
                  <span className="font-semibold text-juv-deep-chestnut">Description</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((item, index) => (
                <SitemapTableRow 
                  key={`${item.category}-${item.title}`} 
                  item={item} 
                  index={index}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </motion.div>

      {/* No Results */}
      {sortedData.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-8 text-gray-500"
        >
          No pages found matching your criteria
        </motion.div>
      )}
    </div>
  );
};
